import React, { useEffect, useMemo, useState } from "react";
import { Modal, Select, Spin, Input, Upload, message, Progress } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { userService, propertyService } from "@iso/services";
import { fileShareService } from "@iso/services/fileShare.service";
import { getFileName } from "@iso/components/FileBrowser/fileUtils";

const { Option } = Select;
const { Dragger } = Upload;
const { TextArea } = Input;

const MAX_MESSAGE_LENGTH = 1000;

/**
 * Share an existing library file, or upload a new one and share it in
 * a single submit. When `existingFile` is set the picker is hidden and
 * we POST { attachment_id }; otherwise we POST a multipart upload.
 *
 * Pass either `propertyId` or `corporateGroupId` (mirroring AssignUserModal)
 * to scope the recipient picker.
 */
const ShareFileModal = ({
  visible,
  existingFile = null,
  propertyId = null,
  corporateGroupId = null,
  onCancel,
  onSuccess,
}) => {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [recipientIds, setRecipientIds] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [fileToUpload, setFileToUpload] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const currentUserId = useMemo(() => {
    try {
      const raw = window.localStorage.getItem("user");
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.id ? Number(parsed.id) : null;
    } catch (err) {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!visible) return;
    setRecipientIds([]);
    setMessageText("");
    setFileToUpload(null);
    setUploadProgress(0);

    const filterSelf = (list) =>
      currentUserId ? list.filter((u) => Number(u.id) !== currentUserId) : list;

    if (corporateGroupId) {
      setLoadingUsers(true);
      propertyService
        .getAllProperties()
        .then((propsRes) => {
          const properties = propsRes?.properties || [];
          const ids = properties
            .filter((p) => p.corporate_group_id === corporateGroupId)
            .map((p) => p.id);
          if (ids.length === 0) {
            setUsers([]);
            return null;
          }
          return propertyService.getUsersFromMultipleProperties(ids);
        })
        .then((res) => {
          if (res && res.code === "0000" && Array.isArray(res.users)) {
            const seen = new Set();
            const deduped = [];
            for (const u of res.users) {
              if (!seen.has(u.id)) {
                seen.add(u.id);
                deduped.push(u);
              }
            }
            setUsers(filterSelf(deduped));
          } else if (res !== null) {
            setUsers([]);
          }
        })
        .catch(() => {
          message.error("Failed to load corporate group users");
          setUsers([]);
        })
        .finally(() => setLoadingUsers(false));
      return;
    }

    if (!propertyId) {
      setUsers([]);
      return;
    }
    setLoadingUsers(true);
    userService
      .getUserForProperty(propertyId)
      .then((res) => {
        if (res && res.code === "0000" && Array.isArray(res.users)) {
          setUsers(filterSelf(res.users));
        } else {
          setUsers([]);
        }
      })
      .catch(() => {
        message.error("Failed to load property users");
        setUsers([]);
      })
      .finally(() => setLoadingUsers(false));
  }, [visible, propertyId, corporateGroupId, currentUserId]);

  const formatLabel = (user) => {
    const name = `${user.name || ""} ${user.surname || ""}`.trim();
    return name || user.email || `User #${user.id}`;
  };

  const canSubmit =
    recipientIds.length > 0 && (existingFile || fileToUpload) && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      let res;
      if (existingFile) {
        res = await fileShareService.shareExistingFile({
          attachmentId: existingFile.id,
          recipientUserIds: recipientIds,
          message: messageText.trim() || null,
          propertyId,
        });
      } else {
        res = await fileShareService.uploadAndShare({
          file: fileToUpload,
          recipientUserIds: recipientIds,
          message: messageText.trim() || null,
          propertyId,
          onProgress: setUploadProgress,
        });
      }
      if (res && (res.code === "0000" || res.success)) {
        message.success(
          recipientIds.length > 1
            ? `Shared with ${recipientIds.length} users`
            : "File shared"
        );
        if (onSuccess) onSuccess(res);
      } else {
        message.error(res?.message || "Failed to share file");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to share file");
    } finally {
      setSubmitting(false);
    }
  };

  const fileLabel = existingFile ? getFileName(existingFile) : null;

  return (
    <Modal
      title={
        existingFile
          ? `Share "${fileLabel}"`
          : "Upload and share a file"
      }
      visible={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      okText="Share"
      okButtonProps={{ disabled: !canSubmit }}
      confirmLoading={submitting}
      destroyOnClose
      zIndex={1200}
      wrapClassName="file-manager-bulk-modal"
    >
      <Spin spinning={loadingUsers}>
        {!existingFile && (
          <div style={{ marginBottom: 16 }}>
            <Dragger
              multiple={false}
              maxCount={1}
              beforeUpload={(file) => {
                setFileToUpload(file);
                return false;
              }}
              onRemove={() => {
                setFileToUpload(null);
                return true;
              }}
              fileList={fileToUpload ? [fileToUpload] : []}
              disabled={submitting}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag a file here to upload
              </p>
              <p className="ant-upload-hint">
                Opens your device's file picker. The recipient(s) get
                read-only access — you can revoke at any time.
              </p>
            </Dragger>
            {submitting && uploadProgress > 0 && uploadProgress < 100 && (
              <Progress percent={uploadProgress} size="small" />
            )}
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <label
            style={{
              display: "block",
              marginBottom: 4,
              fontWeight: 500,
            }}
          >
            Recipients
          </label>
          <Select
            mode="multiple"
            showSearch
            style={{ width: "100%" }}
            placeholder="Select one or more users"
            value={recipientIds}
            onChange={(values) => setRecipientIds(values)}
            dropdownStyle={{ zIndex: 1300 }}
            filterOption={(input, option) =>
              option?.children
                ?.toString()
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            disabled={submitting}
          >
            {users.map((user) => (
              <Option key={user.id} value={user.id}>
                {formatLabel(user)}
              </Option>
            ))}
          </Select>
        </div>

        <div>
          <label
            style={{
              display: "block",
              marginBottom: 4,
              fontWeight: 500,
            }}
          >
            Message <span style={{ color: "#999" }}>(optional)</span>
          </label>
          <TextArea
            rows={3}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            maxLength={MAX_MESSAGE_LENGTH}
            showCount
            placeholder="Add a short note for the recipient(s)"
            disabled={submitting}
          />
        </div>

        <div style={{ marginTop: 12, fontSize: 12, color: "#888" }}>
          Shared files are recorded for audit. Recipients can preview /
          download until you revoke the share.
        </div>
      </Spin>
    </Modal>
  );
};

export default ShareFileModal;
